import cx from 'classnames';
import React, {useState, useRef, useMemo} from 'react';
import {priority} from '@priority-software/priority-proto-api/lib';
import {Input} from '@priority-software/priority-style-react';
import styles from '../../styles/ToDoListConent.module.scss';
import {Record} from '../../../../stores/Form/Record';
import {apiLocker} from '../../../../stores/Form/API_Locker';
import {keyCodes} from '../../../../constants';
import {
    getThousandSign,
    formatNumber,
    cleanThousandSigns,
    getMaxNumberLength
} from '../../../../features/Form/utils/number';
import {isValid as isValidNumber} from '@priority-software/priority-utils/lib/number';
import {monitor} from '../../consts';
import {sendMonitor} from '../../../../utils/monitoring';

type PriorityInputProps = {
    record: Record;
    column: priority.netitems.form.IColumn;
    updatePriorityField: (record: Record, value: string) => void;
};

export const PriorityInput = ({record, column, updatePriorityField}: PriorityInputProps) => {
    const [value, setValue] = useState(
        record.getField(column.ID as number)?.origValue?.toString() || '0'
    );

    const maxNumberLength = useMemo(() => {
        const maxLength = column.Width as number;
        const decimal = column.Decimal as number;
        return getMaxNumberLength({maxLength, decimal});
    }, [column]);

    const fieldRef: React.Ref<any> = useRef();

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.keyCode === keyCodes.ENTER || e.keyCode === keyCodes.ESC) {
            e.preventDefault();
            e.stopPropagation();
            fieldRef.current?.inputRef?.current?.blur();
        }
    };

    const validateInput = value => {
        return isValidNumber(
            cleanThousandSigns(value),
            column.Decimal,
            maxNumberLength,
            true,
            getThousandSign()
        );
    };

    const handleChange = e => {
        if (!apiLocker.isLock(record?.parentForm?.session)) {
            if (validateInput(e.target?.value)) {
                setValue(e.target.value);
            }
        }
    };

    const handleBlur = async () => {
        const initialValue = record.getField(column.ID as number)?.origValue || '0';
        const currentValue = cleanThousandSigns(value) || '0';
        const shouldSave = currentValue !== initialValue;
        if (shouldSave) {
            if (!value) {
                setValue('0');
            } else {
                setValue(formatNumber(currentValue));
            }
            await updatePriorityField(record, currentValue);
        }
    };

    const handleFocus = () => {
        if (apiLocker.isLock(record?.parentForm.session)) {
            fieldRef.current?.inputRef?.current?.blur();
        }
        sendMonitor(monitor.TODOLIST_Priority);
    };

    return (
        <div className={cx(styles.mainColumn, {[styles.disabled]: column.Readonly})}>
            <Input
                autoSelect
                value={value}
                onBlur={handleBlur}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
                onChange={handleChange}
                componentClasses={styles.priorityInput}
                ref={fieldRef}
            />
        </div>
    );
};
